import type { ToolResultCard } from "@/api/chat";
import { Badge } from "@/components/ui/badge";
import {
  ActionIcon,
  CompactActionLabel,
  type ActionData,
} from "@/components/workflows/action-display";
import {
  CircleDotIcon,
  PlayCircleIcon,
  SkipForwardIcon,
} from "lucide-react";

// ─── Type guards ─────────────────────────────────────────────

export function isToolResultCard(
  result: unknown,
): result is ToolResultCard {
  return (
    typeof result === "object" &&
    result !== null &&
    "card_type" in result &&
    "data" in result
  );
}

// ─── Card data types ─────────────────────────────────────────

type WorkflowPreviewData = {
  name: string;
  is_enabled: boolean;
  project_field_name: string | null;
  field_values: string[];
  actions: ActionData[];
  match_count: number | null;
};

type DryRunProject = {
  name: string;
  id: string;
  status: "run" | "skip";
};

type DryRunResultsData = {
  workflow_name: string;
  is_enabled: boolean;
  total_matching: number;
  would_run: number;
  would_skip: number;
  projects: DryRunProject[];
};

// ─── Card registry ───────────────────────────────────────────

const CARD_COMPONENTS: Record<
  string,
  React.FC<{ data: Record<string, unknown> }>
> = {
  workflow_preview: WorkflowPreviewCard,
  dry_run_results: DryRunResultsCard,
};

export function ToolCard({ card }: { card: ToolResultCard }) {
  const Component = CARD_COMPONENTS[card.card_type];
  if (!Component) return null;
  return <Component data={card.data} />;
}

// ─── Workflow Preview Card ───────────────────────────────────

function WorkflowPreviewCard({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const {
    name,
    is_enabled: isEnabled,
    project_field_name: projectFieldName,
    field_values: fieldValues = [],
    actions = [],
    match_count: matchCount,
  } = data as unknown as WorkflowPreviewData;

  return (
    <div className="mt-2 rounded-lg border bg-background/80 p-3 text-xs">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-foreground">{name}</span>
        <Badge
          variant={isEnabled ? "default" : "secondary"}
          className="h-5 text-[10px]"
        >
          {isEnabled ? "Active" : "Draft"}
        </Badge>
      </div>

      {/* Field filter */}
      <div className="mb-2 flex flex-wrap items-center gap-1">
        {projectFieldName && fieldValues.length > 0 ? (
          <>
            <span className="text-[10px] font-medium text-muted-foreground">
              {projectFieldName}:
            </span>
            {fieldValues.map((val) => (
              <Badge
                key={val}
                variant="outline"
                className="h-5 text-[10px] font-normal"
              >
                {val.replace(/_/g, " ")}
              </Badge>
            ))}
          </>
        ) : (
          <Badge variant="outline" className="h-5 text-[10px] font-normal">
            All projects
          </Badge>
        )}
      </div>

      {/* Action pipeline */}
      {actions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {actions.map((action, i) => (
            <div key={i} className="flex items-center gap-1">
              {i > 0 && (
                <span className="text-muted-foreground/50">&rarr;</span>
              )}
              <div className="inline-flex items-center gap-1 rounded border bg-muted/50 px-1.5 py-0.5">
                <ActionIcon type={action.type} className="h-3 w-3 flex-shrink-0" />
                <CompactActionLabel action={action} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Match count */}
      {matchCount !== null && matchCount !== undefined && (
        <div className="mt-2 flex items-center gap-1 text-muted-foreground">
          <CircleDotIcon className="h-3 w-3" />
          <span>
            Matches <strong className="text-foreground">{matchCount}</strong>{" "}
            project{matchCount !== 1 ? "s" : ""}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Dry Run Results Card ────────────────────────────────────

function DryRunResultsCard({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const {
    workflow_name: workflowName,
    is_enabled: isEnabled,
    total_matching: totalMatching,
    would_run: wouldRun,
    would_skip: wouldSkip,
    projects = [],
  } = data as unknown as DryRunResultsData;

  return (
    <div className="mt-2 rounded-lg border bg-background/80 p-3 text-xs">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-foreground">
          Dry Run: {workflowName}
        </span>
        <Badge
          variant={isEnabled ? "default" : "secondary"}
          className="h-5 text-[10px]"
        >
          {isEnabled ? "Active" : "Draft"}
        </Badge>
      </div>

      {/* Summary stats */}
      <div className="mb-2 flex gap-4">
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-blue-500" />
          <span>{totalMatching} matching</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-green-500" />
          <span>{wouldRun} would run</span>
        </div>
        {wouldSkip > 0 && (
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-gray-400" />
            <span>{wouldSkip} would skip</span>
          </div>
        )}
      </div>

      {/* Project list */}
      {projects.length > 0 && (
        <div className="max-h-40 space-y-1 overflow-y-auto">
          {projects.map((project) => (
            <div
              key={project.id}
              className="flex items-center justify-between rounded px-2 py-1 odd:bg-muted/30"
            >
              <span className="truncate">{project.name}</span>
              {project.status === "run" ? (
                <span className="flex items-center gap-1 text-green-600">
                  <PlayCircleIcon className="h-3 w-3" />
                  Run
                </span>
              ) : (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <SkipForwardIcon className="h-3 w-3" />
                  Skip
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {totalMatching > projects.length && (
        <div className="mt-1 text-center text-muted-foreground">
          + {totalMatching - projects.length} more project
          {totalMatching - projects.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}
